/**
 * The quiz editor's question list, as pure functions: the shape the editor works with (options in an
 * array, the right answer as a letter) and the shape the API stores (options keyed A–F).
 */

export const OPTION_KEYS = ['A', 'B', 'C', 'D', 'E', 'F'];

// Each option stays on its own letter. The CRM drops blank options when it saves but keeps the letters
// of the others, so a gap is kept here as an empty slot: closing it up would move every later option
// up a letter and the stored answer letter would point at a different option.
function optionsFromApi(options) {
  const slots = OPTION_KEYS.map((k) => options?.[k] ?? '');
  let last = slots.length - 1;
  while (last >= 0 && !slots[last]) last -= 1;
  return slots.slice(0, last + 1);
}

const tempId = () => Date.now() + Math.random();

export function normalizeQuestion(q) {
  const options = Array.isArray(q.options) ? q.options : optionsFromApi(q.options);
  // A question stored without an answer gets none here either: the save then asks for one, rather
  // than quietly grading against A
  const fromIndex = Number.isInteger(q.correctAnswers?.[0]) ? OPTION_KEYS[q.correctAnswers[0]] : '';
  const correctKey = q.correctAnswer || fromIndex || '';
  return {
    id: q.id || tempId(),
    type: q.type || 'MULTIPLE_CHOICE',
    text: q.text || '',
    options,
    correctAnswer: correctKey,
    explanation: q.explanation || '',
    // The question's picture (the card's MediaSlot). `imageTouched` marks a change in this editor.
    imageUrl: q.imageUrl || null,
    imageAlt: q.imageAlt || null,
  };
}

export function toApiQuestion(q) {
  const entries = q.options.map((o, i) => [OPTION_KEYS[i], o.trim()]).filter(([, o]) => o);
  return {
    // Only a server id (a string) is sent: the backend uses it to carry the question's picture into the
    // new version. A question added in this editor has a temporary numeric id that means nothing there.
    ...(typeof q.id === 'string' && { id: q.id }),
    text: q.text.trim(),
    type: 'MULTIPLE_CHOICE',
    options: Object.fromEntries(entries),
    correctAnswer: q.correctAnswer,
    explanation: q.explanation?.trim() || undefined,
    // An untouched picture is left out (the backend carries the version's picture over by id); a
    // picture changed or removed here is sent, null removing it.
    ...(q.imageTouched ? { imageUrl: q.imageUrl || null } : q.imageUrl && { imageUrl: q.imageUrl }),
    ...(q.imageAlt && { imageAlt: q.imageAlt }),
  };
}

/**
 * Options and answer letter of a question once option `optIdx` is removed. Letters follow position, so
 * every option after the removed one moves up a letter and the answer letter moves with it. When the
 * answer itself is removed the letter is cleared: the teacher has to pick a new one (see hasCorrectAnswer).
 */
export function withoutOption(q, optIdx) {
  const answerIdx = OPTION_KEYS.indexOf(q.correctAnswer);
  let correctAnswer = q.correctAnswer;
  if (answerIdx === optIdx) correctAnswer = '';
  else if (answerIdx > optIdx) correctAnswer = OPTION_KEYS[answerIdx - 1];
  return { options: q.options.filter((_, i) => i !== optIdx), correctAnswer };
}

/** True when the question's answer letter names an option that has text. */
export function hasCorrectAnswer(q) {
  const idx = OPTION_KEYS.indexOf(q.correctAnswer);
  return idx >= 0 && Boolean(q.options[idx]?.trim());
}

/**
 * A new, empty question. No option is marked right: a default answer (it used to be A) is a wrong
 * answer key nobody chose, so the teacher has to mark one before the quiz saves.
 */
export function newQuestion() {
  return {
    id: tempId(),
    type: 'MULTIPLE_CHOICE',
    text: '',
    options: ['', '', '', ''],
    correctAnswer: '',
    explanation: '',
    imageUrl: null,
    imageAlt: null,
  };
}

/**
 * A copy of a question, placed after it. It gets a temporary id, so it saves as a question of its own,
 * and keeps the picture, which the teacher copied along with the text.
 */
export function copyQuestion(q) {
  return { ...q, id: tempId(), options: [...q.options] };
}

/** An editor question from a line of the "Ro'yxatdan qo'shish" dialog ({ text, options, correctIndex }). */
export function fromParsedQuestion(parsed) {
  return {
    ...newQuestion(),
    text: parsed.text,
    options: [...parsed.options],
    correctAnswer: OPTION_KEYS[parsed.correctIndex] || '',
  };
}

export const MAX_TIME_LIMIT_MINUTES = 360;

/**
 * The time limit field: empty is no limit (null), otherwise a whole number of minutes from 1 to 360,
 * the range the API accepts.
 */
export function readTimeLimit(input) {
  const raw = String(input ?? '').trim();
  if (!raw) return { valid: true, minutes: null };
  const n = Number(raw);
  const valid = Number.isInteger(n) && n >= 1 && n <= MAX_TIME_LIMIT_MINUTES;
  return { valid, minutes: valid ? n : null };
}

/** The pass mark field: empty means the backend default; otherwise a whole percentage, 0 to 100. */
export function readPassMark(input) {
  const raw = String(input ?? '').trim();
  if (!raw) return { valid: true, value: null };
  const n = Number(raw);
  const valid = Number.isInteger(n) && n >= 0 && n <= 100;
  return { valid, value: valid ? n : null };
}
